/**
 * Visual Artifact Library Metadata
 *
 * Derives compact Library metadata for visual artifact interactive items.
 *
 * Primary responsibilities:
 * - Turn validated visual artifacts into sidebar-friendly labels
 * - Keep workflow action labels type-specific and deterministic
 *
 * Dependencies:
 * - Visual artifact validation and types
 */

import type { VisualArtifact } from './types'
import { isVisualArtifact } from './validation'

export type VisualArtifactLibraryAction = {
  id: string
  label: string
}

export type VisualArtifactLibraryMetadata = {
  typeLabel: string
  detail: string
  confidenceLabel?: string
  sourceLabel?: string
  actions: VisualArtifactLibraryAction[]
}

// --------------

export function getVisualArtifactLibraryMetadata(
  value: unknown
): VisualArtifactLibraryMetadata | null {
  if (!isVisualArtifact(value)) {
    return null
  }

  const shared = getSharedMetadata(value)

  switch (value.type) {
    case '3d-model':
      return {
        ...shared,
        typeLabel: '3D model',
        detail: getThreeDModelDetail(value),
        actions: [
          { id: 'open-model', label: 'Open model' },
          { id: 'export-model', label: 'Export' },
          { id: 'edit-materials', label: 'Edit materials' }
        ]
      }
    case 'schematic':
      return {
        ...shared,
        typeLabel: 'Schematic',
        detail: `${formatCount(value.data.nodes.length, 'node')} / ${formatCount(
          value.data.edges.length,
          'link'
        )}`,
        actions: [
          { id: 'open-schematic', label: 'Open schematic' },
          { id: 'create-tasks', label: 'Create tasks' }
        ]
      }
    case 'address-map':
      return {
        ...shared,
        typeLabel: 'Address map',
        detail: formatCount(value.data.markers.length, 'address', 'addresses'),
        actions: [
          { id: 'open-map', label: 'Open map' },
          { id: 'export-addresses', label: 'Export addresses' },
          { id: 'create-tasks', label: 'Create tasks' }
        ]
      }
    case 'heatmap':
      return {
        ...shared,
        typeLabel: 'Heatmap',
        detail: `${formatCount(value.data.points.length, 'point')} / ${value.data.bounds.width} x ${value.data.bounds.height}`,
        actions: [
          { id: 'open-heatmap', label: 'Open heatmap' },
          { id: 'create-segment', label: 'Create segment' }
        ]
      }
    case 'route-map':
      return {
        ...shared,
        typeLabel: 'Route map',
        detail: `${formatCount(value.data.stops.length, 'stop')} / ${formatCount(
          value.data.routes.length,
          'route'
        )}`,
        actions: [
          { id: 'open-route', label: 'Open route' },
          { id: 'assign-reps', label: 'Assign reps' },
          { id: 'schedule-visits', label: 'Schedule visits' }
        ]
      }
    case 'territory-map':
      return {
        ...shared,
        typeLabel: 'Territory map',
        detail: `${formatCount(value.data.territories.length, 'territory', 'territories')}${
          value.data.markers?.length
            ? ` / ${formatCount(value.data.markers.length, 'address', 'addresses')}`
            : ''
        }`,
        actions: [
          { id: 'open-territory', label: 'Open territory' },
          { id: 'assign-owners', label: 'Assign owners' },
          { id: 'create-campaign', label: 'Create campaign' }
        ]
      }
    case 'blueprint':
      return {
        ...shared,
        typeLabel: 'Blueprint',
        detail: `${formatCount(value.data.elements.length, 'element')} / ${value.data.bounds.width} x ${value.data.bounds.height} ${value.data.unit}`,
        actions: [
          { id: 'open-blueprint', label: 'Open blueprint' },
          { id: 'edit-dimensions', label: 'Edit dimensions' },
          { id: 'export-plan', label: 'Export plan' }
        ]
      }
  }
}

// --------------

function getSharedMetadata(artifact: VisualArtifact) {
  const confidenceLabel =
    artifact.data.confidence === undefined
      ? undefined
      : `${Math.round(clamp01(artifact.data.confidence) * 100)}% confidence`
  const sourceLabel = artifact.data.sourceNotes?.length
    ? formatCount(artifact.data.sourceNotes.length, 'source note')
    : undefined

  return {
    confidenceLabel,
    sourceLabel
  }
}

function getThreeDModelDetail(artifact: Extract<VisualArtifact, { type: '3d-model' }>) {
  const bounds = artifact.data.bounds
  const boundsLabel = bounds
    ? ` / ${bounds.width} x ${bounds.height} x ${bounds.depth}${
        artifact.data.unit ? ` ${artifact.data.unit}` : ''
      }`
    : ''

  return `${artifact.data.format.toUpperCase()}${boundsLabel}`
}

function clamp01(value: number) {
  return Math.min(Math.max(value, 0), 1)
}

function formatCount(count: number, singular: string, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`
}
